import React, { useEffect, useState } from 'react';

import { useWindowVirtualizer } from '@tanstack/react-virtual';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useBreakpoint } from '~/techstyle-shared/react-components';

import {
  MAX_CARD_WIDTH,
  GAP_BETWEEN_GRID_COLUMNS_MOBILE,
  GAP_BETWEEN_GRID_ROWS,
  GRID_CARD_TYPES,
  OUTER_GRID_PADDING_MOBILE,
  RESIZE_THROTTLE,
  VIRTUAL_SCROLL_OVERSCAN,
  VIRTUAL_SCROLL_GRID_ID,
} from '../constants';
import useGridInputs from '../hooks/useGridInputs';
import useNumGridCols from '../hooks/useNumGridCols';
import { getCellHeight } from '../utils/measurements';

import GridCardRenderer from './GridCardRenderer';
import NoResultsFound from './NoResultsFound';
import ProductCardSkeleton from './ProductCard/ProductCardSkeleton';

const VirtualScrollWrapper = styled.div`
  margin: 0 16px;
`;

// POC Reference: https://github.com/Fabletics/web-spa/pull/3883/files#diff-dfe3140fb2e0ace5e8430d5ba8ce5c99925d14e33102bc49717d1373b7d340f3

// TODO: consider making this one single component to handle both mobile and desktop
// IMPORTANT: make sure to not use expensive operations in this component. This component re-renders
// on scroll and resize per the virtualizer. There are optimizations with the virtualizer that does
// not re-paint the entire grid but it will re-render the items in view so avoid expensive operations
// in this component. This is especially true for any hooks, even if the hook has caching mechanisms.
// The hook will still be called on every render, so avoid hooks in this component
// If you need to use hooks, use them in the parent component and pass down the data.
const MobileGridVirtualScroll = ({
  fetchNextPage = () => {},
  hasNextPage,
  isPrimaryProductsFetching,
  isPrimaryProductsLoading,
  virtualList = [],
  handleOpenFilterDrawer = () => {},
}) => {
  const [dynamicCardWidth, setDynamicCardWidth] = useState(MAX_CARD_WIDTH);
  const numGridCols = useNumGridCols();
  const { isMobile } = useBreakpoint();
  const { isQuickViewEnabled } = useGridInputs();

  const cellHeight = getCellHeight({
    isMobile,
    dynamicCardWidth,
    isQuickViewEnabled,
  });

  useEffect(() => {
    const handleResize = throttle(() => {
      // this is preferred over using window.innerWidth so that the browser inspector works with mobile device emulation
      // this also excludes the scrollbar width ~15-17px if exists
      const windowWidth = document.documentElement.clientWidth;

      const virtualScrollWidth = windowWidth - OUTER_GRID_PADDING_MOBILE;

      const gapBetweenAllGridColumns =
        GAP_BETWEEN_GRID_COLUMNS_MOBILE * (numGridCols - 1);

      const perCardWidth =
        (virtualScrollWidth - gapBetweenAllGridColumns) / numGridCols;

      setDynamicCardWidth(perCardWidth);
    }, RESIZE_THROTTLE);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel(); // clean up the throttle
    };
  }, [numGridCols]);

  const getRowHeight = (index) => {
    const virtualRowItems = virtualList[index]; // each virtualRow will actually be rendered as a grid of items
    const assets = virtualRowItems.filter(
      (item) => item.type === GRID_CARD_TYPES.ASSET
    );

    if (!assets?.length) {
      return cellHeight; // rowSpan = 1 for non-asset items (product cards)
    }
    const minAssetRowIndex = Math.min(
      ...assets.map((item) => item.effectiveCustomVars?.row)
    );
    const maxAssetRowIndex = Math.max(
      ...assets.map((item) => item.effectiveCustomVars?.row + item.span.rows)
    );
    const rowSpan = maxAssetRowIndex - minAssetRowIndex;

    // TODO: consider handling gaps between each virtual row which is actually a grid.
    // we currently are accounting gaps for multi-row and multi-column assets
    // but not for gaps between virtual rows
    const rowHeight =
      cellHeight * rowSpan + GAP_BETWEEN_GRID_ROWS * (rowSpan - 1);

    return rowHeight;
  };

  const virtualizer = useWindowVirtualizer({
    count: virtualList?.length || 0, // consider adding another row for loading skeleton, need to handle gaps with last row due to marketing assets
    estimateSize: (index) => getRowHeight(index),
    overscan: VIRTUAL_SCROLL_OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    virtualizer.measure();
  }, [dynamicCardWidth, virtualizer]);

  useEffect(() => {
    if (virtualItems.length === 0) {
      return;
    }

    const lastItem = virtualItems.at(-1);
    if (!lastItem) {
      return;
    }

    const shouldFetchNextPage =
      lastItem.index >= virtualList?.length - 1 &&
      hasNextPage &&
      !isPrimaryProductsFetching;
    if (shouldFetchNextPage) {
      // make sure to not fetch if we are already fetching
      fetchNextPage();
    }
  }, [
    hasNextPage,
    isPrimaryProductsFetching,
    fetchNextPage,
    virtualItems,
    virtualList.length,
  ]);

  if (isPrimaryProductsLoading) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${numGridCols}, 1fr)`,
          gap: `${GAP_BETWEEN_GRID_ROWS}px ${GAP_BETWEEN_GRID_COLUMNS_MOBILE}px`,
          padding: '0px 16px',
        }}
      >
        {Array.from({ length: numGridCols * 3 }).map((_, index) => {
          return (
            <ProductCardSkeleton
              key={index}
              width={dynamicCardWidth}
              height={cellHeight}
            />
          );
        })}
      </div>
    );
  }

  if (!virtualList?.length && !isPrimaryProductsLoading) {
    return <NoResultsFound onClickButton={handleOpenFilterDrawer} />;
  }

  return (
    <VirtualScrollWrapper>
      <div
        id={VIRTUAL_SCROLL_GRID_ID}
        style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const rowIndex = virtualRow.index;
          const rowItems = virtualList[rowIndex];

          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: virtualRow.start,
                width: '100%',
                height: virtualRow.size,
              }}
            >
              <div
                style={{
                  display: 'grid', // rows will dynamically grow based on the item span
                  gridTemplateColumns: `repeat(${numGridCols}, 1fr)`,
                  gap: `${GAP_BETWEEN_GRID_ROWS}px ${GAP_BETWEEN_GRID_COLUMNS_MOBILE}px`,
                }}
              >
                {rowItems?.map((item, column) => {
                  return (
                    <GridCardRenderer
                      key={item.id}
                      item={item}
                      dynamicCardWidth={dynamicCardWidth}
                      row={rowIndex}
                      column={column}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </VirtualScrollWrapper>
  );
};

MobileGridVirtualScroll.propTypes = {
  fetchNextPage: PropTypes.func,
  handleOpenFilterDrawer: PropTypes.func,
  hasNextPage: PropTypes.bool,
  isPrimaryProductsFetching: PropTypes.bool,
  isPrimaryProductsLoading: PropTypes.bool,
  virtualList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        type: PropTypes.string,
        span: PropTypes.shape({
          rows: PropTypes.number,
          columns: PropTypes.number,
        }),
      })
    )
  ),
};

export default MobileGridVirtualScroll;
